import React from 'react';
import styled from 'styled-components';

import Button from '../Button';

import { Dropdown as StatusIcon } from '@icons/common';

export interface DropdownProps {
  className?: string;
  headRenderer: React.ReactNode;
  contentRenderer?: React.ReactNode | null;
  open?: boolean;
  onToggle: (isOpen: boolean) => void;
}

interface DropdownState {
  isOpen: boolean;
  isOpenProp: boolean;
}

class Dropdown extends React.PureComponent<DropdownProps, DropdownState> {
  private refObj: React.RefObject<HTMLButtonElement>;

  static defaultProps = {
    className: '',
    contentRenderer: null,
    open: false,
  };

  static getDerivedStateFromProps(
    nextProps: Readonly<DropdownProps & { open: boolean }>,
    prevState: DropdownState
  ) {
    const { open: isOpen } = nextProps;
    const { isOpenProp } = prevState;

    if (isOpen !== isOpenProp) {
      return {
        isOpenProp: isOpen,
        isOpen,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      isOpen: props.open,
      isOpenProp: props.open,
    };

    this.refObj = React.createRef();
    this.handleBlur = this.handleBlur.bind(this);
    this.handleOpen = this.handleOpen.bind(this);
  }

  handleBlur({ relatedTarget }) {
    const { onToggle } = this.props;
    const { isOpen } = this.state;
    if (isOpen && !this.refObj?.current?.contains(relatedTarget)) {
      this.setState(
        {
          isOpen: false,
        },
        () => onToggle(false)
      );
    }
  }

  handleOpen() {
    const { onToggle } = this.props;
    this.setState(
      prevState => ({
        isOpen: !prevState.isOpen,
      }),
      () => onToggle(true)
    );
  }

  render() {
    const { headRenderer, contentRenderer, ...props } = this.props;

    const { isOpen } = this.state;

    return (
      <StyledDropdown {...props} ref={this.refObj} onBlur={this.handleBlur}>
        <StyledButton functional onClick={this.handleOpen}>
          {headRenderer}
          {contentRenderer && <StyledDropdownIcon open={isOpen} />}
        </StyledButton>
        {contentRenderer && (
          <StyledContent hidden={!isOpen}>{contentRenderer}</StyledContent>
        )}
      </StyledDropdown>
    );
  }
}

const StyledDropdown = styled.button`
  cursor: pointer;
  position: relative;
`;

export const StyledDropdownIcon = styled(StatusIcon)<{ open: boolean }>`
  width: 9px;
  height: 5px;
  margin: 3px 0 0 5px;

  path {
    fill: #000;
  }

  ${({ open }) =>
    open &&
    `
    transform: rotate(180deg);
  `}
`;

export const StyledButton = styled(Button)`
  width: 100%;
  height: 100%;
  display: flex;
  cursor: pointer;
  align-items: center;
`;

export const StyledContent = styled.div<{ hidden: boolean }>`
  left: 50%;
  z-index: 2;
  display: flex;
  padding: 10px;
  margin-top: 10px;
  border-radius: 4px;
  position: absolute;
  background-color: #fff;
  transform: translateX(-50%);
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.1);

  ::before {
    border-bottom: 7px solid #fff;
    border-left: 8px solid transparent;
    border-right: 8px solid transparent;
    content: '';
    height: 0;
    left: 50%;
    margin-left: -8px;
    position: absolute;
    top: -6px;
    width: 0;
  }

  ${({ hidden }) =>
    hidden &&
    `
    display: none;
  `}
`;

export default Dropdown;

// export default React.forwardRef<HTMLButtonElement, DropdownProps>(
//   (props, ref) => <Dropdown {...props} ref={ref} />
// );
